/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

class ImageMagnifier {
    constructor(element, config = {}) {
        this.element = element;
        this.asset = this.element.querySelector('.js-img-mag__asset');
        this.ready = false;
        this.scale = 1;
        this.intervalId = null;
        this.moving = false;
        this.moveEvent = null;

        // Config options
        this.config = {
            disableOnDesktop: config.disableOnDesktop || false,
            mobileZoomScale: config.mobileZoomScale || 1.5,
            desktopZoomScale: config.desktopZoomScale || 1.2,
        };

        this.init();
    }

    init() {
        this.asset.addEventListener('load', () => this.initMove());

        if (this.asset.complete) {
            this.initMove();
        }
    }

    initMove() {
        if (this.ready) return;
        this.ready = true;
        this.calculateScale();

        if (!this.config.disableOnDesktop || this.isMobile()) {
            this.element.addEventListener('mouseenter', this.handleEvent.bind(this));
        }
    }

    calculateScale() {
        const customScale = this.element.getAttribute('data-scale');
        if (customScale) {
            this.scale = parseFloat(customScale);
        } else {
            if (this.isMobile()) {
                this.scale = this.config.mobileZoomScale;
            } else if (this.config.disableOnDesktop) {
                this.scale = 1; // Default zoom disabled
            } else {
                this.scale = this.asset.naturalWidth / this.element.offsetWidth;
                this.scale = Math.floor(this.scale * 100) / 100;
                if (this.scale > this.config.desktopZoomScale) this.scale -= 0.2;
            }
        }
    }

    isMobile() {
        return window.matchMedia('(max-width: 768px)').matches;
    }

    initImageMove() {
        this.moveEvent = this.handleEvent.bind(this);
        this.element.addEventListener('mousemove', this.moveEvent);
        this.element.addEventListener('mouseleave', this.moveEvent);
    }

    cancelImageMove() {
        if (this.intervalId) {
            if (!window.requestAnimationFrame) {
                clearInterval(this.intervalId);
            } else {
                window.cancelAnimationFrame(this.intervalId);
            }
            this.intervalId = null;
        }

        if (this.moveEvent) {
            this.element.removeEventListener('mousemove', this.moveEvent);
            this.element.removeEventListener('mouseleave', this.moveEvent);
            this.moveEvent = null;
        }
    }

    handleEvent(event) {
        switch (event.type) {
            case 'mouseenter':
                this.startMove(event);
                break;
            case 'mousemove':
                this.move(event);
                break;
            case 'mouseleave':
                this.endMove();
                break;
        }
    }

    startMove(event) {
        this.moving = true;
        this.initImageMove();
        this.zoom(event);
    }

    move(event) {
        if (!this.moving || this.intervalId) return;

        if (!window.requestAnimationFrame) {
            this.intervalId = setTimeout(() => this.zoom(event), 250);
        } else {
            this.intervalId = window.requestAnimationFrame(() => this.zoom(event));
        }
    }

    endMove() {
        this.moving = false;
        this.cancelImageMove();
        this.asset.removeAttribute('style');
    }

    zoom(event) {
        const rect = this.element.getBoundingClientRect();
        let translateX = rect.left - event.clientX;
        let translateY = rect.top - event.clientY;

        translateX = Math.max(Math.min(translateX, 0), -rect.width);
        translateY = Math.max(Math.min(translateY, 0), -rect.height);

        const transform = `translateX(${translateX * (this.scale - 1)}px) translateY(${translateY * (this.scale - 1)}px) scale(${this.scale})`;
        this.asset.style.transform = transform;
        this.intervalId = null;
    }
}

// Example of initializing ImageMagnifier with options
const imageMagnifiers = document.querySelectorAll('.js-img-mag');

if (imageMagnifiers) {
    imageMagnifiers.forEach(element =>
        new ImageMagnifier(element, {
            disableOnDesktop: true,
            mobileZoomScale: 2,
            desktopZoomScale: 1.0,
        })
    );
}

class ScrollHandler {
    constructor() {
        this.callbacks = [];
        this.lastKnownScrollPosition = 0;
        this.ticking = false;
        this.handleScroll = this.handleScroll.bind(this);
        window.addEventListener('scroll', this.handleScroll, { passive: true });
    }

    /**
    * Registers a new scroll callback.
    * @param {Function} callback - Function to execute on scroll.
    */
    register(callback) {
        if (typeof callback === 'function') {
            this.callbacks.push(callback);
        }
    }

    /**
    * Scroll event handler.
    */
    handleScroll() {
        this.lastKnownScrollPosition = window.scrollY;

        if (!this.ticking) {
            window.requestAnimationFrame(() => {
                this.callbacks.forEach(callback => callback(this.lastKnownScrollPosition));
                this.ticking = false;
            });
            this.ticking = true;
        }
    }

    /**
    * Unregisters a scroll callback.
    * @param {Function} callback - Function to remove.
    */
    unregister(callback) {
        this.callbacks = this.callbacks.filter(cb => cb !== callback);
    }

    /**
    * Cleans up event listeners.
    */
    destroy() {
        window.removeEventListener('scroll', this.handleScroll);
        this.callbacks = [];
    }
}

const scrollHandler = new ScrollHandler();

const stickyAddToCart = document.querySelector('[data-sticky-add-to-cart]');
const productBlockListElement = document.querySelector('.product-block-list');

function stickyAddToCartVisibility(scrollPos) {
    const triggerOffset = window.innerWidth <= 768 // Adjust width as needed for your mobile breakpoint
        ? productBlockListElement.offsetHeight - 1250 // Smaller trigger for mobile
        : productBlockListElement.offsetHeight - 300; // Default trigger for larger screens

    if (scrollPos > triggerOffset) {       
        stickyAddToCart.classList.add('visible');
    } else {
        stickyAddToCart.classList.remove('visible');
    }
}

if (stickyAddToCart) {
    scrollHandler.register(stickyAddToCartVisibility);
}


document.addEventListener('DOMContentLoaded', function() {
    // Select the Add to Cart button within the sticky add-to-cart section
    var stickyAddToCartButton = document.querySelector('[data-sticky-add-to-cart-button] button');
    
    // **Add Quantity Button Functionality**
    
    // Select the quantity input field
    var quantityInput = document.querySelector('[data-sticky-add-to-cart-quantity] input[name="quantity"]');
    
    // Select the increase and decrease buttons
    var decreaseButton = document.querySelector('[data-sticky-add-to-cart-quantity] [data-action="decrease-picker-quantity"]');
    var increaseButton = document.querySelector('[data-sticky-add-to-cart-quantity] [data-action="increase-picker-quantity"]');
    
    if (decreaseButton && increaseButton && quantityInput) {
      decreaseButton.addEventListener('click', function(event) {
        event.preventDefault();
        var currentValue = parseInt(quantityInput.value) || 1;
        if (currentValue > 1) {
          quantityInput.value = currentValue - 1;
          // Optionally synchronize with main quantity input
          syncQuantityInputs(quantityInput.value);
        }
      });
  
      increaseButton.addEventListener('click', function(event) {
        event.preventDefault();
        var currentValue = parseInt(quantityInput.value) || 1;
        quantityInput.value = currentValue + 1;
        // Optionally synchronize with main quantity input
        syncQuantityInputs(quantityInput.value);
      });
    }
  
    // Event listener for the sticky add-to-cart button
    if (stickyAddToCartButton) {
      stickyAddToCartButton.addEventListener('click', function(event) {
        event.preventDefault(); // Prevent the default form submission
  
        // Call the function to add the item to the cart and dispatch events
        addProductAndDispatchEvents();
      });
    }
  
    function addProductAndDispatchEvents() {
      // Get the quantity from the sticky quantity selector
      var quantityInput = document.querySelector('[data-sticky-add-to-cart-quantity] input[name="quantity"]');
      var quantity = quantityInput ? parseInt(quantityInput.value) : 1;
  
      // Get the selected variant object
      var variant = getCurrentVariant();
  
      // Ensure variant is valid
      if (!variant || !variant.id) {
        alert('Please select a variant.');
        return;
      }
  
      // Check if the variant is available
      if (typeof variant.available !== 'undefined' && !variant.available) {
        alert('This variant is currently unavailable.');
        return;
      }
  
      // Disable the button and show loading state
      var stickyAddToCartButton = document.querySelector('[data-sticky-add-to-cart-button] button');
      stickyAddToCartButton.disabled = true;
      stickyAddToCartButton.classList.add('loading');
  
      // Add the item to the cart via AJAX
      addItemToCart(variant.id, quantity)
        .then(function(data) {
          // Re-enable the button and remove loading state
          stickyAddToCartButton.disabled = false;
          stickyAddToCartButton.classList.remove('loading');
  
          // Dispatch 'product:added' event
          document.documentElement.dispatchEvent(new CustomEvent('product:added', {
            bubbles: true,
            detail: {
              variant: variant,
              quantity: quantity
            }
          }));
  
          // Dispatch 'cart:refresh' event to update the cart UI
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true
          }));
  
          // Optionally, display success message
          console.log('Product added to cart successfully!');
        })
        .catch(function(error) {
          // Re-enable the button and remove loading state
          stickyAddToCartButton.disabled = false;
          stickyAddToCartButton.classList.remove('loading');
  
          // Handle errors
          alert('Error adding to cart: ' + error.message);
        });
    }
  
    function addItemToCart(variantId, quantity) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      })
      .then(function(response) {
        if (!response.ok) {
          return response.json().then(function(error) {
            throw new Error(error.description);
          });
        }
        return response.json();
      });
    }
  
    function getCurrentVariant() {
      // Option A: Using window.currentVariant
      if (window.currentVariant) {
        return window.currentVariant;
      }
  
      // Option B: From variant select input
      var variantSelect = document.querySelector('select[name="id"]');
      if (variantSelect) {
        var variantId = parseInt(variantSelect.value);
        // Retrieve the variant object from the product's variants array
        if (window.productVariants) {
          return window.productVariants.find(function(variant) {
            return variant.id === variantId;
          });
        } else {
          return { id: variantId }; // Fallback if variants array is not available
        }
      }
  
      // Option C: From data attributes
      var variantElement = document.querySelector('[data-current-variant-id]');
      if (variantElement) {
        var variantId = parseInt(variantElement.getAttribute('data-current-variant-id'));
        return { id: variantId };
      }
  
      // If no variant found
      return null;
    }
  
    // **Optional: Synchronize Quantity Inputs**
    function syncQuantityInputs(newQuantity) {
      // Main quantity input in the main product form
      var mainQuantityInput = document.querySelector('.main-product-form input[name="quantity"]');
  
      if (mainQuantityInput) {
        mainQuantityInput.value = newQuantity;
      }
    }
  });

  (function() {
	var Modal = function(element) {
		this.element = element;
		this.triggers = document.querySelectorAll('[aria-controls="'+this.element.getAttribute('id')+'"]');
		this.firstFocusable = null;
		this.lastFocusable = null;
		this.moveFocusEl = null; // focus will be moved to this element when modal is open
		this.modalFocus = this.element.getAttribute('data-modal-first-focus') ? this.element.querySelector(this.element.getAttribute('data-modal-first-focus')) : null;
		this.selectedTrigger = null;
		this.preventScrollEl = this.getPreventScrollEl();
		this.showClass = "modal--is-visible";
		this.initModal();
	};

	Modal.prototype.getPreventScrollEl = function() {
		var scrollEl = false;
		var querySelector = this.element.getAttribute('data-modal-prevent-scroll');
		if(querySelector) scrollEl = document.querySelector(querySelector);
		return scrollEl;
	};

	Modal.prototype.initModal = function() {
		var self = this;
		//open modal when clicking on trigger buttons
		if ( this.triggers ) {
			for(var i = 0; i < this.triggers.length; i++) {
				this.triggers[i].addEventListener('click', function(event) {
					event.preventDefault();
					if(self.element.classList.contains(self.showClass)) {
						self.closeModal();
						return;
					}
					self.selectedTrigger = event.currentTarget;
					self.showModal();
					self.initModalEvents();
				});
			}
		}

		// listen to the openModal event -> open modal without a trigger button
		this.element.addEventListener('openModal', function(event){
			if(event.detail) self.selectedTrigger = event.detail;
			self.showModal();
			self.initModalEvents();
		});

		// listen to the closeModal event -> close modal without a trigger button
		this.element.addEventListener('closeModal', function(event){
			if(event.detail) self.selectedTrigger = event.detail;
			self.closeModal();
		});

		// if modal is open by default -> initialise modal events
		if(this.element.classList.contains(this.showClass)) this.initModalEvents();
	};

	Modal.prototype.showModal = function() {
		var self = this;
		this.element.classList.add(this.showClass);;
		this.getFocusableElements();
		if(this.moveFocusEl) {
			this.moveFocusEl.focus();
			// wait for the end of transitions before moving focus
			this.element.addEventListener("transitionend", function cb(event) {
				self.moveFocusEl.focus();
				self.element.removeEventListener("transitionend", cb);
			});
		}
		this.emitModalEvents('modalIsOpen');
		// change the overflow of the preventScrollEl
		if(this.preventScrollEl) this.preventScrollEl.style.overflow = 'hidden';
	};

	Modal.prototype.closeModal = function() {
		if(!this.element.classList.contains(this.showClass)) return;
		this.element.classList.remove(this.showClass);
		this.firstFocusable = null;
		this.lastFocusable = null;
		this.moveFocusEl = null;
		if(this.selectedTrigger) this.selectedTrigger.focus();
		//remove listeners
		this.cancelModalEvents();
		this.emitModalEvents('modalIsClose');
		// change the overflow of the preventScrollEl
		if(this.preventScrollEl) this.preventScrollEl.style.overflow = '';
	};

	Modal.prototype.initModalEvents = function() {
		//add event listeners
		this.element.addEventListener('keydown', this);
		this.element.addEventListener('click', this);
	};

	Modal.prototype.cancelModalEvents = function() {
		//remove event listeners
		this.element.removeEventListener('keydown', this);
		this.element.removeEventListener('click', this);
	};

	Modal.prototype.handleEvent = function (event) {
		switch(event.type) {
			case 'click': {
				this.initClick(event);
			}
			case 'keydown': {
				this.initKeyDown(event);
			}
		}
	};

	Modal.prototype.initKeyDown = function(event) {
		if( event.keyCode && event.keyCode == 9 || event.key && event.key == 'Tab' ) {
			//trap focus inside modal
			this.trapFocus(event);
		} else if( (event.keyCode && event.keyCode == 13 || event.key && event.key == 'Enter') && event.target.closest('.js-modal__close')) {
			event.preventDefault();
			this.closeModal(); // close modal when pressing Enter on close button
		}	
	};

	Modal.prototype.initClick = function(event) {
		//close modal when clicking on close button or modal bg layer 
		if( !event.target.closest('.js-modal__close') && !event.target.classList.contains('js-modal') ) return;
		event.preventDefault();
		this.closeModal();
	};

	Modal.prototype.trapFocus = function(event) {
		if( this.firstFocusable == document.activeElement && event.shiftKey) {
			//on Shift+Tab -> focus last focusable element when focus moves out of modal
			event.preventDefault();
			this.lastFocusable.focus();
		}
		if( this.lastFocusable == document.activeElement && !event.shiftKey) {
			//on Tab -> focus first focusable element when focus moves out of modal
			event.preventDefault();
			this.firstFocusable.focus();
		}
	}

	Modal.prototype.getFocusableElements = function() {
		//get all focusable elements inside the modal
		var allFocusable = this.element.querySelectorAll(focusableElString);
		this.getFirstVisible(allFocusable);
		this.getLastVisible(allFocusable);
		this.getFirstFocusable();
	};

	Modal.prototype.getFirstVisible = function(elements) {
		//get first visible focusable element inside the modal
		for(var i = 0; i < elements.length; i++) {
			if( isVisible(elements[i]) ) {
				this.firstFocusable = elements[i];
				break;
			}
		}
	};

	Modal.prototype.getLastVisible = function(elements) {
		//get last visible focusable element inside the modal
		for(var i = elements.length - 1; i >= 0; i--) {
			if( isVisible(elements[i]) ) {
				this.lastFocusable = elements[i];
				break;
			}
		}
	};

	Modal.prototype.getFirstFocusable = function() {
		if(!this.modalFocus || !Element.prototype.matches) {
			this.moveFocusEl = this.firstFocusable;
			return;
		}
		var containerIsFocusable = this.modalFocus.matches(focusableElString);
		if(containerIsFocusable) {
			this.moveFocusEl = this.modalFocus;
		} else {
			this.moveFocusEl = false;
			var elements = this.modalFocus.querySelectorAll(focusableElString);
			for(var i = 0; i < elements.length; i++) {
				if( isVisible(elements[i]) ) {
					this.moveFocusEl = elements[i];
					break;
				}
			}
			if(!this.moveFocusEl) this.moveFocusEl = this.firstFocusable;
		}
	};

	Modal.prototype.emitModalEvents = function(eventName) {
		var event = new CustomEvent(eventName, {detail: this.selectedTrigger});
		this.element.dispatchEvent(event);
	};

	function isVisible(element) {
		return element.offsetWidth || element.offsetHeight || element.getClientRects().length;
	};

	window.Modal = Modal;

	//initialize the Modal objects
	var modals = document.getElementsByClassName('js-modal');
	// generic focusable elements string selector
	var focusableElString = '[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable], audio[controls], video[controls], summary';
	if( modals.length > 0 ) {
		var modalArrays = [];
		for( var i = 0; i < modals.length; i++) {
			(function(i){modalArrays.push(new Modal(modals[i]));})(i);
		}

		window.addEventListener('keydown', function(event){ //close modal window on esc
			if(event.keyCode && event.keyCode == 27 || event.key && event.key.toLowerCase() == 'escape') {
				for( var i = 0; i < modalArrays.length; i++) {
					(function(i){modalArrays[i].closeModal();})(i);
				};
			}
		});
	}
}());
  
document.addEventListener('DOMContentLoaded', () => {
    const scrollerInner = document.querySelector('.scroller__inner');
    const scrollLeftBtn = document.querySelector('.scroll-btn.left');
    const scrollRightBtn = document.querySelector('.scroll-btn.right');
    const scrollAmount = 150;
    
    if (scrollLeftBtn && scrollRightBtn) {
        const updateButtons = () => {
          const scrollLeft = scrollerInner.scrollLeft;
          const scrollWidth = scrollerInner.scrollWidth;
          const clientWidth = scrollerInner.clientWidth;
      
          // Disable left button if scrolled to the start
          scrollLeftBtn.disabled = scrollLeft === 0;
      
          // Disable right button if scrolled to the end
          scrollRightBtn.disabled = scrollLeft + clientWidth >= scrollWidth;
        };
      
        scrollLeftBtn.addEventListener('click', () => {
          scrollerInner.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        });
      
        scrollRightBtn.addEventListener('click', () => {
          scrollerInner.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        });
      
        // Update buttons state on scroll
        scrollerInner.addEventListener('scroll', updateButtons);
      
        // Initial button state update
        updateButtons();
    }

  });
  